import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CancelIcon from '@mui/icons-material/Cancel';
import Divider from "@mui/material/Divider";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from "@mui/material/IconButton";
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Table from '../helpers/Table'
import DeletePopup from "../helpers/DeletePopup";

import { addDriver, editDriver, deleteDriver, clearQueryResult } from "../../actions/valueActions";

import { formatString, convertBoolDiffValue } from "./valueFunctions";
// import firstCharToUpper from "../helpers/stringFormat";
import CostValueDriverInvestmentForm from "./CostValueDriverInvestmentForm";
import { getIconFromDriverName } from "./iconFunctions";
import ModalElement from "../Modal";
import AddInvestmentData from "./AddInvestmentData";
import { toolTipUserIcon } from "./iconFunctions";
import { getAllOutcomes } from "./valueFunctions";

const CostValueDriverUpgrade = ({ id, valueModel, costs, driver, outcome }) => {
    const formikRef = useRef(null);
    const dispatch = useDispatch();

    const [open, setOpen] = React.useState(false);
    const [selectedCost, setSelectedCost] = useState(Object.keys(costs)[0])
    const [deleteUpgradeDriverPopupOpen, setDeleteUpgradeDriverPopupOpen] = useState(false);
    const [deleteUpgradeDriverId, setDeleteUpgradeDriverId] = useState(null);
    const [editUpgradeDriverId, setEditUpgradeDriverId] = useState(null);
    const [addInvestmentModalOpen, setAddInvestmentModalOpen] = useState(false);

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleCostChange = (event) => {
        setSelectedCost(event.target.value)
        if (editUpgradeDriverId) {
            setEditUpgradeDriverId(null)
            if (formikRef.current) {
                formikRef.current.resetForm();
            }
        }
    }

    const openDeleteUpgradeDriver = (id) => {
        setDeleteUpgradeDriverId(id)
        setDeleteUpgradeDriverPopupOpen(true)
    }

    const editUpgradeDriver = (id) => {
        setEditUpgradeDriverId(id)
        let upgradeDriver = valueModel.cost_value_driver.upgrade_savings.find(upgradeDriver => upgradeDriver.id === id)
        setSelectedCost(upgradeDriver.sub_cost)
    }

    const toggleAddInvestmentModalOpen = () => {
        setAddInvestmentModalOpen(!addInvestmentModalOpen)
        dispatch(clearQueryResult())
    }

    function renderAddInvestmentModal() {
        return (
            <ModalElement
                modalOpen={addInvestmentModalOpen}
                modalWidth={1000}
                handleClose={toggleAddInvestmentModalOpen}
                header="Add Investment Costs"
                instruction="Please specify what you would like to add and provide a wish that will bring some context to your request"
                content={
                    <AddInvestmentData
                        id={id}
                        industry={valueModel?.industry}
                        type='upgrade'
                        cost_category={driver}
                        costTypes={costs}
                        handleClose={toggleAddInvestmentModalOpen}
                    />
                }
            />

        )
    }

    const onSubmitUpgrade = async (values, driver, cost, { setSubmitting, setErrors }) => {
        // Clone the values object to avoid direct mutation
        const clonedValues = { ...values };
        clonedValues.id = editUpgradeDriverId;
        if (outcome) {
            clonedValues.outcome = outcome;
        }
        clonedValues.cost = driver;
        clonedValues.sub_cost = cost;
        clonedValues.multiple_value = values.multiple_value.toString().replace(/,/g, "");
        clonedValues.units_needed = values.units_needed.toString().replace(/,/g, "");

        let success = false

        if (editUpgradeDriverId) {
            const result = await dispatch(editDriver('upgrade', id, clonedValues, { setSubmitting, setErrors }))
            success = result?.success;
            if (success) {
                setEditUpgradeDriverId(null)
            }
        }
        else {
            const result = await dispatch(addDriver('upgrade', id, clonedValues, { setSubmitting, setErrors }))
            success = result?.success;
        }

        if (formikRef.current && success) {
            formikRef.current.resetForm();
        }
    }

    const handleResetUpgrade = () => {
        setEditUpgradeDriverId(null)

        // Access the formikRef current value and call resetForm
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
    };

    const upgradeDriverColumns = [
        { id: 'outcome', label: 'Outcome', minWidth: 170, action: false },
        { id: 'driver_name', label: 'Name', minWidth: 170, action: false },
        // { id: 'sub_cost', label: 'Type', format: (value) => formatString(value), minWidth: 150, action: false },
        // { id: 'multiple_type', label: 'Project type', format: (value) => firstCharToUpper(value), minWidth: 150, action: false },
        // { id: 'useful_life', label: 'Useful life', format: (value) => value + ' years', minWidth: 100, action: false },
        { id: 'efficiency_gain', label: 'Cost change', minWidth: 100, action: false },
        { id: 'project_shortening', label: 'Project length change', minWidth: 100, action: false },
        { id: 'positive_driver', label: 'Differentiation value', minWidth: 100, format: (value) => convertBoolDiffValue(Boolean(value)), action: false },
        { id: 'actions', label: 'Actions', minWidth: 100, action: false },
    ];

    const createUpgradeDriverData = (id, outcome, driver_name, sub_cost, multiple_type, useful_life, efficiency_gain, project_shortening, positive_driver, actions) => {
        project_shortening = `${project_shortening} days ${positive_driver ? 'shorter' : 'longer'}`

        if (efficiency_gain > 0) {
            efficiency_gain = `${efficiency_gain}% ${positive_driver ? 'saving' : 'increase'}`
        } else {
            efficiency_gain = `${efficiency_gain}%`
        }
        return { id, outcome, driver_name, sub_cost, multiple_type, useful_life, efficiency_gain, project_shortening, positive_driver, actions };
    }

    const upgradeDriverActions = (id) => {
        return (
            <React.Fragment>
                <IconButton size="small" onClick={() => editUpgradeDriver(id)}>
                    <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => openDeleteUpgradeDriver(id)}>
                    <DeleteIcon />
                </IconButton>
            </React.Fragment>
        )
    }

    const upgradeDriverRows = valueModel?.cost_value_driver.upgrade_savings.filter(driver => !outcome || driver.outcome === outcome).map((driver) => {
        return (
            createUpgradeDriverData(driver.id, driver.outcome, driver.driver_name, driver.sub_cost, driver.multiple_type, driver.useful_life, driver.efficiency_gain, driver.project_shortening, driver.positive_driver, upgradeDriverActions(driver.id))
        )
    })

    const upgradeDriverTable = () => {
        return (
            <React.Fragment>
                <Divider />
                <Table
                    columns={upgradeDriverColumns}
                    rows={upgradeDriverRows}
                    selectedId={editUpgradeDriverId}
                />
            </React.Fragment>
        )
    }

    const upgradeDriverFormActions = () => {
        return (
            <React.Fragment>
                {editUpgradeDriverId ? null
                    : <Button variant="contained" startIcon={<Box component={getIconFromDriverName(driver)} />} size="small" type="submit">Add driver</Button>
                }
                {editUpgradeDriverId ?
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" startIcon={<EditIcon />} size="small" type="submit">Save</Button>
                        <Button variant="contained" startIcon={<CancelIcon />} size="small" onClick={handleResetUpgrade}>Cancel</Button>
                    </Stack>
                    : null
                }
            </React.Fragment>
        )
    }

    return (
        <Box>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Stack gap={1} direction="row" sx={{ ml: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        Driver type:
                    </Typography>
                    <Typography variant="caption">
                        Capital Investment Savings
                    </Typography>
                </Stack>
                <Box sx={{ width: '30%' }} />
                <Button startIcon={<AutoFixHighIcon />} onClick={toggleAddInvestmentModalOpen} sx={{ textTransform: 'none' }}>Add Investments</Button>
            </Box>

            <Stack direction="column" gap={1} sx={{ ml: 2, mt: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    1. Select Type
                </Typography>
                <Box>
                    <Select
                        open={open}
                        onClose={handleClose}
                        onOpen={handleOpen}
                        value={selectedCost}
                        onChange={handleCostChange}
                        size='small'
                    >
                        {Object.keys(costs).map((cost) => (
                            <MenuItem key={cost} value={cost}>
                                {/* {formatString(cost)} */}
                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                    {formatString(cost)}
                                    {costs[cost]?.user_created && toolTipUserIcon()}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </Stack>

            <CostValueDriverInvestmentForm
                currency="€"
                outcome={outcome}
                outcomeList={getAllOutcomes(valueModel?.driver_analysis)}
                ownProduct={valueModel.product}
                costStructure={costs[selectedCost]}
                editInvestmentDriverId={editUpgradeDriverId}
                editInvestmentDriver={valueModel?.cost_value_driver.upgrade_savings.find(upgradeDriver => upgradeDriver.id === editUpgradeDriverId)}
                formActions={upgradeDriverFormActions}
                onSubmit={(values, { setSubmitting, setErrors }) => onSubmitUpgrade(values, driver, selectedCost, { setSubmitting, setErrors })}
                ref={formikRef}
            />
            {upgradeDriverTable()}
            <DeletePopup open={deleteUpgradeDriverPopupOpen} onClose={() => setDeleteUpgradeDriverPopupOpen(false)} onDelete={() => dispatch(deleteDriver('upgrade', id, deleteUpgradeDriverId))} />
            {renderAddInvestmentModal()}
        </Box>
    )
}

export default CostValueDriverUpgrade